import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import BlogPostCard from "@/components/blog/BlogPostCard";
import BlogBreadcrumb from "@/components/blog/BlogBreadcrumb";
import TagNavigation from "@/components/blog/TagNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featureImage?: string | null;
  featureImageAlt?: string;
  publishedAt: string;
  readingTime?: number;
  featured?: boolean;
  primaryTag?: {
    name: string;
    slug: string;
  } | null;
  primaryAuthor?: {
    name: string;
    slug: string;
    profileImage?: string | null;
  };
}

interface Tag {
  id: string;
  name: string;
  slug: string;
  postCount: number;
}

interface Pagination {
  page: number;
  pages: number;
  total: number;
  next: number | null;
  prev: number | null;
}

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { getLocalizedPath } = useLocalizedPath();
  const [posts, setPosts] = useState<Post[]>([]);
  const [featuredPost, setFeaturedPost] = useState<Post | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const currentPage = parseInt(searchParams.get("page") || "1");

  // Determine language from URL
  const isEnglish = location.pathname.startsWith("/en");
  const currentLang = isEnglish ? "en" : "th";

  const SITE_URL = "https://carbonwize.io";
  const canonicalUrl = currentLang === "en" ? `${SITE_URL}/en/blog` : `${SITE_URL}/blog`;
  const thUrl = `${SITE_URL}/blog`;
  const enUrl = `${SITE_URL}/en/blog`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch tags for navigation
        const tagsResponse = await supabase.functions.invoke("fetch-blog-tags");
        if (tagsResponse.data?.success) {
          setTags(tagsResponse.data.tags || []);
        }

        // Fetch featured post (only on first page)
        if (currentPage === 1) {
          const featuredResponse = await supabase.functions.invoke("fetch-blog-posts", {
            body: null,
          });
          // Find featured post from the response
          if (featuredResponse.data?.success) {
            const allPosts = featuredResponse.data.posts || [];
            const featured = allPosts.find((p: Post) => p.featured);
            if (featured) {
              setFeaturedPost(featured);
            }
          }
        }

        // Fetch posts
        const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-posts`);
        url.searchParams.set("page", currentPage.toString());
        url.searchParams.set("limit", "12");
        url.searchParams.set("lang", currentLang);

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        });

        const data = await response.json();

        if (data?.success) {
          // Filter out featured post from list if showing on first page
          const postList = data.posts || [];
          setPosts(
            currentPage === 1 && featuredPost ? postList.filter((p: Post) => p.id !== featuredPost?.id) : postList,
          );
          setPagination(data.pagination);
        }
      } catch (error) {
        console.error("Error fetching blog data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  const handleLoadMore = async () => {
    if (!pagination?.next) return;

    setLoadingMore(true);
    try {
      const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-posts`);
      url.searchParams.set("page", pagination.next.toString());
      url.searchParams.set("limit", "12");
      url.searchParams.set("lang", currentLang);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
      });

      const data = await response.json();

      if (data?.success) {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error("Error loading more posts:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang={currentLang} />
        <title>Blog | Carbonwize - Carbon Accounting & Sustainability Insights</title>
        <meta
          name="description"
          content="Explore the latest insights on carbon accounting, sustainability reporting, ESG compliance, and climate action from the Carbonwize team."
        />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="th" href={thUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={thUrl} />
        <meta property="og:title" content="Blog | Carbonwize" />
        <meta
          property="og:description"
          content="Explore the latest insights on carbon accounting, sustainability reporting, and ESG compliance."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://carbonwize.io/og-thumbnail.png" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={currentLang === "th" ? "th_TH" : "en_US"} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <BlogBreadcrumb items={[{ label: "Blog" }]} />

            <header className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-cta mb-4">Our Blog</h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Insights, guides, and updates on carbon accounting, sustainability, and ESG compliance
              </p>
            </header>

            {/* Tag Navigation */}
            {tags.length > 0 && (
              <div className="mb-8">
                <TagNavigation tags={tags.slice(0, 8)} />
              </div>
            )}

            {loading ? (
              <div className="space-y-8">
                {/* Row 1: Hero skeleton (1 post) */}
                <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                  <div className="md:flex">
                    <Skeleton className="h-64 md:w-1/2" />
                    <div className="p-8 md:w-1/2 space-y-4">
                      <Skeleton className="h-8 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </div>

                {/* Row 2: 2 posts skeleton */}
                <div className="grid md:grid-cols-2 gap-8">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                      <Skeleton className="h-48 w-full" />
                      <div className="p-6 space-y-3">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Row 3+: 3 posts skeleton */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                      <Skeleton className="h-48 w-full" />
                      <div className="p-6 space-y-3">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {(() => {
                  // Determine hero post (featured or first post)
                  const heroPost = featuredPost || (posts.length > 0 ? posts[0] : null);
                  // Get remaining posts (exclude hero)
                  const remainingPosts = heroPost ? posts.filter((p) => p.id !== heroPost.id) : posts;
                  // Row 2: first 2 of remaining
                  const row2Posts = remainingPosts.slice(0, 2);
                  // Row 3+: rest
                  const row3Posts = remainingPosts.slice(2);

                  return (
                    <>
                      {/* Row 1: Hero Post (1 full-width) */}
                      {heroPost && (
                        <section>
                          <BlogPostCard post={heroPost} featured showAuthor />
                        </section>
                      )}

                      {/* Row 2: 2 posts */}
                      {row2Posts.length > 0 && (
                        <section>
                          <div className="grid md:grid-cols-2 gap-8">
                            {row2Posts.map((post) => (
                              <BlogPostCard key={post.id} post={post} />
                            ))}
                          </div>
                        </section>
                      )}

                      {/* Row 3+: 3 posts per row */}
                      {row3Posts.length > 0 && (
                        <section>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {row3Posts.map((post) => (
                              <BlogPostCard key={post.id} post={post} />
                            ))}
                          </div>
                        </section>
                      )}

                      {posts.length === 0 && !heroPost && (
                        <div className="text-center py-12">
                          <p className="text-muted-foreground">No posts found.</p>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Load More */}
                {pagination?.next && (
                  <div className="text-center">
                    <Button variant="outline" size="lg" onClick={handleLoadMore} disabled={loadingMore}>
                      {loadingMore ? "Loading..." : "Load More"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
